/**
 * Utility functions for building preview URLs with environment prefixes
 */

/**
 * Infers the environment from the current hostname and API URLs
 * Checks for Netlify deploy previews, API URL patterns, etc.
 * @returns Detected environment: "test", "dev", or "prod"
 */
export const inferEnvironment = (): "test" | "dev" | "prod" => {
  // Check current hostname for Netlify deploy preview pattern
  // Pattern: deploy-preview-*--pagoda-labs-bookings-admin-portal.netlify.app
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();

    // Netlify deploy previews are test environment
    if (
      hostname.includes("deploy-preview") &&
      hostname.includes("netlify.app")
    ) {
      return "test";
    }

    // Localhost is dev environment
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "dev";
    }

    // Check for other test/dev patterns in hostname
    if (hostname.includes("test.") || hostname.includes("-test.")) {
      return "test";
    }
    if (hostname.includes("dev.") || hostname.includes("-dev.")) {
      return "dev";
    }
  }

  // Check NODE_ENV for local development (fallback if hostname check didn't work)
  if (process.env.NODE_ENV === "development") {
    return "dev";
  }

  // Final fallback: Check API URLs for environment indicators
  // This helps when hostname detection isn't available or didn't match
  const apiUrls = [
    process.env.REACT_APP_SSG_API_URL || "",
    process.env.REACT_APP_CORE_API_URL || "",
  ];

  for (const url of apiUrls) {
    if (!url) continue; // Skip empty URLs

    const lowerUrl = url.toLowerCase();
    // Check for test environment patterns
    if (
      lowerUrl.includes("-test-") ||
      lowerUrl.includes("test-") ||
      lowerUrl.includes("-test.") ||
      lowerUrl.includes("/test/") ||
      lowerUrl.includes("test.")
    ) {
      return "test";
    }
    // Check for dev environment patterns
    if (
      lowerUrl.includes("-dev-") ||
      lowerUrl.includes("dev-") ||
      lowerUrl.includes("-dev.") ||
      lowerUrl.includes("/dev/") ||
      lowerUrl.includes("dev.")
    ) {
      return "dev";
    }
  }

  // Default to prod if no environment indicator found
  return "prod";
};

/**
 * Gets the environment prefix for preview subdomains
 * @returns Environment prefix string (e.g., "test--", "dev--", or "")
 */
export const getEnvironmentPrefix = (): string => {
  // First check explicit REACT_APP_ENV if set
  const appEnv = (process.env.REACT_APP_ENV || "").toLowerCase();
  if (appEnv === "test") {
    return "test--";
  } else if (appEnv === "dev") {
    return "dev--";
  } else if (appEnv === "prod" || appEnv === "production") {
    return "";
  }

  // If REACT_APP_ENV is not set, infer from hostname and API URLs
  const inferredEnv = inferEnvironment();

  // Local development should use dev prefix
  const isLocalDev = process.env.NODE_ENV === "development";

  if (inferredEnv === "test") {
    return "test--";
  } else if (inferredEnv === "dev" || isLocalDev) {
    return "dev--";
  }

  return "";
};

/**
 * Builds a preview URL for a given subdomain
 * @param subdomain - The subdomain slug (e.g., "crownofbeauty")
 * @returns Full preview URL with environment prefix
 * @example
 * // In test environment:
 * buildPreviewUrl("crownofbeauty") // "https://test--crownofbeauty--preview.getpagoda.site"
 *
 * // In dev environment:
 * buildPreviewUrl("crownofbeauty") // "https://dev--crownofbeauty--preview.getpagoda.site"
 *
 * // In production:
 * buildPreviewUrl("crownofbeauty") // "https://crownofbeauty--preview.getpagoda.site"
 */
export const buildPreviewUrl = (subdomain: string): string => {
  const envPrefix = getEnvironmentPrefix();
  return `https://${envPrefix}${subdomain}--preview.getpagoda.site`;
};
